import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { GameModeService } from '../../../_services/game-mode.service';
import { MiniGameService } from '../../../_services/mini-game.service';
import { ClassStudentsService } from '../../../_services/class-students.service';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { GamePhase } from '../../../interfaces/game-state.interface';
import {
  BowlingMode,
  BowlingParticipant
} from '../../../interfaces/bowling.interface';
import { buildEmptyFrames } from '../../../_services/bowling-score';

/**
 * Pre-game team-builder for the bowling mini-game. Reached when the
 * player picks Bowling from the Fun Games picker (manual flow); the
 * auto-cycle path bypasses this screen entirely and uses
 * `MiniGameService.startBowlingWithAutoTeams` instead.
 *
 * Responsibilities:
 *   1. Pick a mode — `ONE_VS_ONE` (single student per side) or
 *      `TEAM_VS_TEAM` (multi-student rosters that rotate per turn).
 *   2. Drag-drop students from the available pool into side A or B
 *      (Material CDK DragDrop).
 *   3. Edit each side's display name (defaults: "Blue Team" / "Red
 *      Team", bilingual).
 *   4. Optional `randomize()` shortcut to auto-fill both sides.
 *   5. On finalize: build two `BowlingParticipant`s with default
 *      colours + empty frames, and hand off to
 *      `MiniGameService.startBowlingGame(mode, [a, b])` which kicks
 *      off the actual session.
 *
 * Pure UI state — no internal subscriptions to the bowling state
 * itself, only to language. Validation happens in `canFinalize()`
 * before the start button can fire.
 */
@Component({
  selector: 'app-bowling-setup',
  templateUrl: './bowling-setup.component.html',
  styleUrls: ['./bowling-setup.component.scss']
})
export class BowlingSetupComponent implements OnInit, OnDestroy {
  /** Cleanup pipe for the language subscription — emits in
   *  `ngOnDestroy` to unsubscribe everything chained off it. */
  private destroy$ = new Subject<void>();
  /** Active app language ('ar' or 'en') — read once on init then
   *  refreshed by the GlobalService.getLang() subscription. Drives
   *  bilingual labels and which side-name field updates on input. */
  currLang: any = 'en';

  /** Re-export the enum so the template can reference
   *  `BowlingMode.ONE_VS_ONE` etc. via this prop. */
  BowlingMode = BowlingMode;
  /** Currently-selected mode. Defaults to TEAM_VS_TEAM since most
   *  classes have multiple students; the toggle in the template can
   *  switch to 1v1. Switching modes resets all rosters via
   *  `setMode`. */
  mode: BowlingMode = BowlingMode.TEAM_VS_TEAM;

  /** Pool of students not yet assigned to a side — populated from
   *  the current class's roster (or the full school roster if the
   *  class has none) in `ngOnInit`. Drag-drop moves entries between
   *  this list and the side rosters. */
  availableStudents: MiniGameStudent[] = [];
  /** Side A roster — receives students dragged into the left
   *  drop-zone in the template. Capped at 1 entry in 1v1 mode (see
   *  `drop`). */
  sideAStudents: MiniGameStudent[] = [];
  /** Side B roster — same shape as `sideAStudents` but for the
   *  right drop-zone. */
  sideBStudents: MiniGameStudent[] = [];

  /** Bilingual display name for side A — bound to the editable
   *  team-name input. Default "Blue Team" matches the auto-cycle
   *  helper's defaults so the colour palette is consistent across
   *  the app. */
  sideAName = { en: 'Blue Team', ar: 'الفريق الأزرق' };
  /** Bilingual display name for side B — same pattern as side A
   *  with the Red Team default. */
  sideBName = { en: 'Red Team', ar: 'الفريق الأحمر' };
  /** Plain string mirror of `sideAName[currLang]` — used as
   *  `[(ngModel)]` source in the template. The two-way binding
   *  feeds back through `onSideANameChange` which writes the
   *  currLang-specific slot of `sideAName`. */
  sideANameInput = 'Blue Team';
  /** Same as `sideANameInput` but for side B. */
  sideBNameInput = 'Red Team';

  constructor(
    /** Used to read the current class id (for student lookup) and
     *  to navigate back to GAME_SELECT on cancel. */
    private gameModeService: GameModeService,
    /** Receives the finalised participants via `startBowlingGame`. */
    private miniGameService: MiniGameService,
    /** Source of student rosters keyed by class id, with full-school
     *  fallback when the class is empty. */
    private classStudentsService: ClassStudentsService,
    /** Provides the active app language as an observable. */
    private globalService: GlobalService
  ) {}

  /**
   * Subscribes to language changes (so the name input reflects the
   * right language slot) and seeds `availableStudents` from the
   * current class. Falls back to the full school roster if the
   * class has no students — useful in dev/dummy data setups.
   */
  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currLang = lang;
        // Sync the input mirror to the new language so the editable
        // field shows the localised name on lang switch.
        this.sideANameInput = this.currLang === 'ar' ? this.sideAName.ar : this.sideAName.en;
        this.sideBNameInput = this.currLang === 'ar' ? this.sideBName.ar : this.sideBName.en;
      });

    // Seed pool — class first, then school-wide as fallback.
    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) students = this.classStudentsService.getAllStudents();
    // Spread so we own a fresh array — drag-drop mutation won't
    // affect the upstream class roster.
    this.availableStudents = [...students];
  }

  ngOnDestroy(): void {
    // Tear down the language subscription.
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Switch between 1v1 and team modes. Returns early if the user
   * picked the same mode (no-op). On a real switch, every assigned
   * student returns to the available pool — we can't safely keep
   * existing rosters because the constraints differ (1v1 caps each
   * side at 1, team mode allows many). Cleanest UX is to start the
   * roster fresh.
   */
  setMode(mode: BowlingMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    // Empty both sides back into the pool.
    this.availableStudents = [
      ...this.availableStudents,
      ...this.sideAStudents,
      ...this.sideBStudents
    ];
    this.sideAStudents = [];
    this.sideBStudents = [];
  }

  /**
   * Two-way-binding handler for the side A name input. Writes to
   * the active language slot only — the other language slot keeps
   * its previous value (so toggling language shows the previously-
   * typed name in that language, not the freshly-edited one).
   */
  onSideANameChange(value: string): void {
    this.sideANameInput = value;
    if (this.currLang === 'ar') this.sideAName.ar = value;
    else this.sideAName.en = value;
  }

  /** Mirror of `onSideANameChange` for side B. */
  onSideBNameChange(value: string): void {
    this.sideBNameInput = value;
    if (this.currLang === 'ar') this.sideBName.ar = value;
    else this.sideBName.en = value;
  }

  /**
   * CDK DragDrop event handler. Two cases:
   *
   *   1. **Reorder within the same list** — `moveItemInArray` swaps
   *      the dragged student into its new position (e.g. reordering
   *      who bowls first in team mode).
   *   2. **Move between lists** — `transferArrayItem` removes from
   *      the source list and inserts into the target. In 1v1 mode
   *      we early-return if the target side already has a student,
   *      enforcing the one-per-side cap.
   */
  drop(event: CdkDragDrop<MiniGameStudent[]>): void {
    if (event.previousContainer === event.container) {
      // Reorder within same list.
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if (this.mode === BowlingMode.ONE_VS_ONE) {
        // 1v1 cap: refuse drop into a side that already has someone.
        // (Pool always accepts more — only sides are capped.)
        const targetIsSide =
          event.container.id === 'bowling-side-a' || event.container.id === 'bowling-side-b';
        if (targetIsSide && event.container.data.length >= 1) return;
      }
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  /**
   * Auto-fill both sides with a fair shuffle of all available
   * students. Process:
   *   1. Pool every student (available + already-assigned).
   *   2. Fisher-Yates shuffle in-place.
   *   3. Slice based on mode: first 1 + second 1 in 1v1; first
   *      half + second half in team mode.
   *
   * Always leaves `availableStudents` empty in team mode (everyone's
   * placed) and with leftover students in 1v1 mode (only 2 needed).
   */
  randomize(): void {
    const all = [...this.availableStudents, ...this.sideAStudents, ...this.sideBStudents];
    // Fisher-Yates — unbiased shuffle.
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    if (this.mode === BowlingMode.ONE_VS_ONE) {
      // Pick the first two shuffled students; rest stay in the pool.
      this.sideAStudents = all.slice(0, 1);
      this.sideBStudents = all.slice(1, 2);
      this.availableStudents = all.slice(2);
    } else {
      // Split everyone — `Math.ceil` so an odd count puts the extra
      // student on side A (matches the visual order).
      const mid = Math.ceil(all.length / 2);
      this.sideAStudents = all.slice(0, mid);
      this.sideBStudents = all.slice(mid);
      this.availableStudents = [];
    }
  }

  /**
   * Validation gate for the start button. Different shape per mode:
   * 1v1 needs exactly one student on each side; team mode needs at
   * least one. Disabling the start button until this returns true
   * is the cheapest way to prevent invalid sessions.
   */
  canFinalize(): boolean {
    if (this.mode === BowlingMode.ONE_VS_ONE) {
      return this.sideAStudents.length === 1 && this.sideBStudents.length === 1;
    }
    return this.sideAStudents.length >= 1 && this.sideBStudents.length >= 1;
  }

  /**
   * Finalise setup and kick off the bowling session. Builds the
   * two `BowlingParticipant`s from the current state — fresh
   * spreads of names + students (so later edits to this component
   * can't mutate the live game state). Each student is tagged with
   * its side via `teamId` so the in-game UI can colour-code per
   * student avatar.
   *
   * Hands off to `MiniGameService.startBowlingGame` which sets up
   * the session, transitions the phase to MINI_GAME_ACTIVE, and
   * fires the `gameWorld-bowling-launch` Amplitude event.
   *
   * Returns silently if `canFinalize()` is false — the start button
   * should be disabled in that case but this is a defensive guard
   * in case it ever gets called from a keyboard shortcut etc.
   */
  start(): void {
    if (!this.canFinalize()) return;

    const sideA: BowlingParticipant = {
      id: 'A',
      // Spread to detach from the editable inputs.
      displayName: { ...this.sideAName },
      color: '#2196F3',                                // Material Blue 500
      // teamId stamp so per-student UI can colour-code the avatar.
      students: this.sideAStudents.map(s => ({ ...s, teamId: 'A' as const })),
      frames: buildEmptyFrames(),
      currentStudentIdx: 0,
      totalScore: 0
    };
    const sideB: BowlingParticipant = {
      id: 'B',
      displayName: { ...this.sideBName },
      color: '#F44336',                                // Material Red 500
      students: this.sideBStudents.map(s => ({ ...s, teamId: 'B' as const })),
      frames: buildEmptyFrames(),
      currentStudentIdx: 0,
      totalScore: 0
    };

    this.miniGameService.startBowlingGame(this.mode, [sideA, sideB]);
  }

  /**
   * Cancel — return to the GAME_SELECT screen (where the user can
   * pick a different game). Doesn't clear local state in case the
   * user comes back; the component is destroyed on phase change so
   * a fresh instance starts clean anyway.
   */
  goBack(): void {
    this.gameModeService.setPhase(GamePhase.GAME_SELECT);
  }

  /** Bilingual student-name pick — same convention as the rest of
   *  the bowling UI. Used in the drag-drop chip template. */
  getStudentName(student: MiniGameStudent): string {
    return this.currLang === 'ar' ? student.name.ar : student.name.en;
  }

  /** Header label for side A's drop zone. In 1v1 mode shows
   *  "Player 1" / "اللاعب الأول" (no team-name input is shown);
   *  in team mode shows the editable team name. */
  get sideALabel(): string {
    return this.mode === BowlingMode.ONE_VS_ONE
      ? (this.currLang === 'ar' ? 'اللاعب الأول' : 'Player 1')
      : this.sideANameInput;
  }

  /** Header label for side B's drop zone. Mirrors `sideALabel`. */
  get sideBLabel(): string {
    return this.mode === BowlingMode.ONE_VS_ONE
      ? (this.currLang === 'ar' ? 'اللاعب الثاني' : 'Player 2')
      : this.sideBNameInput;
  }
}
